<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { entryRulesAPI, saveEntryRule } from '../api';
import { useCrmStore } from '../store';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const store = useCrmStore();
const inboxes = useMapGetter('inboxes/getInboxes');

const dialogRef = ref(null);
const rules = ref([]);
const isSaving = ref(false);

const inboxOptions = computed(() => [
  { value: '', label: t('WASAPI_CRM.ENTRY_RULES.ANY_INBOX') },
  ...(inboxes.value || []).map(i => ({ value: i.id, label: i.name })),
]);
const stageGroups = computed(() =>
  store.pipelines.map(p => ({
    label: p.name,
    options: p.stages.map(s => ({ value: s.id, label: s.name })),
  }))
);
// O ComboBox é uma lista única: o funil vira prefixo do rótulo, então a busca acha
// tanto por etapa quanto por funil.
const stageOptions = computed(() =>
  stageGroups.value.flatMap(group =>
    group.options.map(option => ({
      value: option.value,
      label: `${group.label} · ${option.label}`,
    }))
  )
);

const fetch = async () => {
  const { data } = await entryRulesAPI.get();
  rules.value = data.map(r => ({ ...r, inbox_id: r.inbox_id || '' }));
};

const open = async () => {
  await fetch();
  dialogRef.value?.open();
};

const addRule = () =>
  rules.value.push({
    id: null,
    stage_id: store.stages[0]?.id || '',
    inbox_id: '',
    ctwa_only: false,
    enabled: true,
  });

const save = async rule => {
  if (!rule.stage_id) return;
  isSaving.value = true;
  try {
    const { data } = await saveEntryRule(rule.id, {
      stage_id: rule.stage_id,
      inbox_id: rule.inbox_id || null,
      ctwa_only: rule.ctwa_only,
      enabled: rule.enabled,
    });
    Object.assign(rule, data, { inbox_id: data.inbox_id || '' });
    useAlert(t('WASAPI_CRM.ENTRY_RULES.SAVED'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('WASAPI_CRM.ENTRY_RULES.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const remove = async (rule, index) => {
  if (rule.id) {
    try {
      await entryRulesAPI.delete(rule.id);
      useAlert(t('WASAPI_CRM.ENTRY_RULES.DELETED'));
    } catch {
      useAlert(t('WASAPI_CRM.ENTRY_RULES.ERROR'));
      return;
    }
  }
  rules.value.splice(index, 1);
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :title="t('WASAPI_CRM.ENTRY_RULES.TITLE')"
    :description="t('WASAPI_CRM.ENTRY_RULES.DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div class="flex flex-col gap-3">
      <p v-if="!rules.length" class="text-sm text-n-slate-11">
        {{ t('WASAPI_CRM.ENTRY_RULES.EMPTY') }}
      </p>
      <div
        v-for="(rule, index) in rules"
        :key="rule.id || `new-${index}`"
        class="flex flex-col gap-2 p-3 rounded-xl outline outline-1 outline-n-weak"
      >
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div class="flex flex-col gap-1">
            <span class="text-xs text-n-slate-11">{{
              t('WASAPI_CRM.ENTRY_RULES.INBOX')
            }}</span>
            <ComboBox
              v-model="rule.inbox_id"
              :options="inboxOptions"
              class="w-full"
            />
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs text-n-slate-11">{{
              t('WASAPI_CRM.ENTRY_RULES.STAGE')
            }}</span>
            <ComboBox
              v-model="rule.stage_id"
              :options="stageOptions"
              class="w-full"
            />
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-4">
          <label class="flex items-center gap-2 text-sm text-n-slate-11">
            <Switch v-model="rule.ctwa_only" />{{
              t('WASAPI_CRM.ENTRY_RULES.CTWA_ONLY')
            }}
          </label>
          <label class="flex items-center gap-2 text-sm text-n-slate-11">
            <Switch v-model="rule.enabled" />{{
              t('WASAPI_CRM.ENTRY_RULES.ENABLED')
            }}
          </label>
          <span class="flex-1" />
          <Button
            icon="i-lucide-trash-2"
            variant="ghost"
            color="ruby"
            size="sm"
            @click="remove(rule, index)"
          />
          <Button
            :label="t('WASAPI_CRM.STAGES.SAVE')"
            color="blue"
            size="sm"
            :disabled="isSaving || !rule.stage_id"
            @click="save(rule)"
          />
        </div>
      </div>
      <Button
        :label="t('WASAPI_CRM.ENTRY_RULES.ADD')"
        icon="i-lucide-plus"
        variant="faded"
        color="slate"
        size="sm"
        class="self-start"
        @click="addRule"
      />
    </div>
    <template #footer>
      <div class="flex justify-end w-full">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="faded"
          color="slate"
          @click="dialogRef.close()"
        />
      </div>
    </template>
  </Dialog>
</template>
